<template>
  <div class="onboarding-form-stepper">
    <ol
      class="onboarding-form-stepper__steps"
      :aria-label="$t('onboarding.wizard.form-steps.aria-label')"
    >
      <template
        v-for="(step, index) in steps"
        :key="step.key"
      >
        <li class="onboarding-form-stepper__step">
          <button
            type="button"
            class="onboarding-form-stepper__button"
            :aria-current="index === currentIndex ? 'step' : undefined"
            :aria-label="step.label"
            :data-testid="`onboarding-form-step-${step.key}`"
            @click="$emit('select', index)"
          >
            <span
              class="onboarding-form-stepper__circle"
              :class="{
                'onboarding-form-stepper__circle--active': index === currentIndex,
                'onboarding-form-stepper__circle--completed':
                  index !== currentIndex && step.complete && index < currentIndex,
              }"
            >
              <v-icon
                v-if="index !== currentIndex && step.complete && index < currentIndex"
                size="16"
                color="white"
              >
                mdi-check
              </v-icon>
              <span v-else>{{ index + 1 }}</span>
            </span>
            <span
              class="onboarding-form-stepper__label text-caption"
              :class="{
                'onboarding-form-stepper__label--active': index === currentIndex,
                'text-medium-emphasis': index !== currentIndex,
              }"
            >
              {{ step.label }}
            </span>
          </button>
        </li>
        <li
          v-if="index < steps.length - 1"
          class="onboarding-form-stepper__line"
          :class="{ 'onboarding-form-stepper__line--completed': index < currentIndex }"
          aria-hidden="true"
        />
      </template>
    </ol>

    <div class="onboarding-form-stepper__progress mt-4">
      <div class="d-flex flex-wrap justify-space-between ga-1 text-caption mb-1">
        <span class="font-weight-medium">
          {{
            $t('onboarding.wizard.form-steps.step-of', {
              current: currentIndex + 1,
              total: steps.length,
            })
          }}
        </span>
        <span class="text-medium-emphasis">
          {{
            $t('onboarding.wizard.form-steps.required-progress', {
              filled: requiredProgress.filled,
              total: requiredProgress.total,
            })
          }}
        </span>
      </div>
      <v-progress-linear
        :model-value="progressPercent"
        color="primary"
        bg-color="primary"
        height="6"
        rounded
        :aria-label="$t('onboarding.wizard.form-steps.progress-aria-label')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

export type OnboardingFormStepperStep = { key: string; label: string; complete: boolean }

const props = defineProps<{
  steps: OnboardingFormStepperStep[]
  currentIndex: number
  requiredProgress: { filled: number; total: number }
}>()

defineEmits<{
  select: [index: number]
}>()

const progressPercent = computed(() =>
  props.requiredProgress.total
    ? Math.round((props.requiredProgress.filled / props.requiredProgress.total) * 100)
    : 100
)
</script>

<style scoped>
.onboarding-form-stepper__steps {
  display: flex;
  align-items: flex-start;
  list-style: none;
  padding: 0;
  margin: 0;
  user-select: none;
}

.onboarding-form-stepper__step {
  flex: 0 0 auto;
  min-width: 0;
}

.onboarding-form-stepper__button {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  max-width: 120px;
  padding: 0;
  background: none;
  border: 0;
  cursor: pointer;
}

.onboarding-form-stepper__button:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 4px;
  border-radius: 8px;
}

.onboarding-form-stepper__circle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  font-size: 13px;
  font-weight: 600;
  background-color: rgba(var(--v-theme-on-surface), 0.08);
  color: rgba(var(--v-theme-on-surface), 0.6);
  transition: all 0.25s ease;
  flex-shrink: 0;
}

.onboarding-form-stepper__circle--active {
  background-color: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-on-primary));
  box-shadow: 0 0 0 4px rgba(var(--v-theme-primary), 0.18);
}

.onboarding-form-stepper__circle--completed {
  background-color: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-on-primary));
}

.onboarding-form-stepper__label {
  text-align: center;
  line-height: 1.2;
}

.onboarding-form-stepper__label--active {
  font-weight: 600;
}

.onboarding-form-stepper__line {
  flex: 1 1 24px;
  height: 2px;
  margin-top: 15px;
  background: rgba(var(--v-theme-on-surface), 0.12);
  transition: background 0.25s ease;
}

.onboarding-form-stepper__line--completed {
  background: rgb(var(--v-theme-primary));
}

/* Phones: keep only the active label so four steps fit without scrolling. */
@media (max-width: 599px) {
  .onboarding-form-stepper__label:not(.onboarding-form-stepper__label--active) {
    display: none;
  }
}
</style>
